import React, { FC, useMemo } from 'react';

import classNames from 'classnames';

import { BUTTON_TYPES, EvTooltip, TITLE_MODES } from '@evinced-private/ui-common';

import { TABLE_LABEL_DAID } from 'data-automation-ids/data-automation-ids';
import { PlatformPopup } from 'src/common/components/platform-popup/PlatformPopup';

import { generateUUID } from '../../helpers/UUIDHelper';

import './DashboardTableLabel.scss';

const SHOW_ELLIPSIS_LENGTH = 18;
const SLICE_LIMIT = 5;

interface IDashboardTableLabelProps {
	isWide?: boolean;
	labels: string[];
	rowTitle?: string;
	rowId?: string;
}

const defaultCb = (f: () => void): void => {
	f();
};

const createEmojiRegex = (): RegExp => {
	try {
		// Modern browsers
		return /^\p{Extended_Pictographic}/u;
	} catch {
		// Heuristic fallback for legacy browsers
		return /^[\u{1F300}-\u{1F6FF}\u{1F900}-\u{1F9FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}]/u;
	}
};

const emojiRegex = createEmojiRegex();

const sortLabels = (a: string, b: string): number => {
	const aIsEmoji = emojiRegex.test(a);
	const bIsEmoji = emojiRegex.test(b);

	if (aIsEmoji && !bIsEmoji) {
		return -1;
	}
	if (!aIsEmoji && bIsEmoji) {
		return 1;
	}

	// Sort A-Z ascending
	return a.localeCompare(b);
};

export const DashboardTableLabel: FC<IDashboardTableLabelProps> = ({
	isWide = false,
	labels,
	rowTitle,
	rowId
}) => {
	const labelsCount = labels.length;
	const extraLabelsCount = labelsCount - SLICE_LIMIT;
	const labelsToRender = labels.slice(0, SLICE_LIMIT);
	const popupTitle = rowTitle ? `Labels for ${rowTitle}` : 'Labels';
	const tableLabelsClassName = classNames('table-labels', { 'table-labels--wide': isWide });

	const sortedLabels = useMemo(() => {
		if (extraLabelsCount <= 0) {
			return labels;
		}
		return [...labels].sort(sortLabels);
	}, [labels, extraLabelsCount]);

	return (
		<div className={tableLabelsClassName}>
			{labelsToRender.map((label) => {
				const showTooltip = label.length > SHOW_ELLIPSIS_LENGTH;
				const uuid = generateUUID();
				const tooltipBaseId = rowId ? `${rowId}-${uuid}` : uuid;
				const tooltipId: string = showTooltip ? tooltipBaseId : undefined;

				if (label.trim().length === 0) {
					return null;
				}

				return (
					<div className="table-label" key={uuid} data-automation-id={TABLE_LABEL_DAID}>
						<span
							data-tooltip-id={tooltipId}
							data-tooltip-content={showTooltip ? label : undefined}
						>
							{label}
						</span>
						{showTooltip && <EvTooltip id={tooltipId} place="bottom" type="info" />}
					</div>
				);
			})}

			{extraLabelsCount > 0 && (
				<PlatformPopup
					buttons={[
						{
							onClick: defaultCb,
							title: 'Close',
							type: 'primary',
							wrapperClassName: 'cancel-button'
						}
					]}
					className="someClass"
					modalRole="alertdialog"
					isCentered
					isBgTransparent
					title={popupTitle}
					titleMode={TITLE_MODES.NORMAL}
					triggerButtonProps={{
						children: `+${extraLabelsCount}`,
						className: 'extra-label',
						disabled: false,
						title: `+${extraLabelsCount}`,
						type: BUTTON_TYPES.GHOST
					}}
				>
					<div className={tableLabelsClassName}>
						{sortedLabels.map((label) => {
							const showTooltip = label.length > SHOW_ELLIPSIS_LENGTH;
							const uuid = generateUUID();
							const tooltipBaseId = rowId ? `${rowId}-${uuid}` : uuid;
							const tooltipId: string = showTooltip ? tooltipBaseId : undefined;

							if (label.trim().length === 0) {
								return null;
							}

							return (
								<div className="table-label" key={uuid}>
									<span
										data-tooltip-id={tooltipId}
										data-tooltip-content={showTooltip ? label : undefined}
										className="popup"
									>
										{label}
									</span>
									{showTooltip && <EvTooltip id={tooltipId} place="bottom" type="info" />}
								</div>
							);
						})}
					</div>
				</PlatformPopup>
			)}
		</div>
	);
};
